import Mod from '@/components/mod.vue'
import { i18n } from '@/i18n'
import { commons } from '@/lib/transformer'
import { closeAllCustomSelects } from '@/composables/useCustomSelects'
import { useUiStore } from '@/stores/ui'
import { useSessionStorage, useConfirmDialog } from '@vueuse/core'
import { merge } from 'lodash'
import { storeToRefs } from 'pinia'
import { computed, createVNode, render, h, toValue } from 'vue'

export const useFSTableCol = col => {
  const { fullscreen } = storeToRefs(useUiStore())

  return computed(() => {
    return fullscreen.value
      ? col.map(c => {
          delete c.width
          return c
        })
      : col
  })
}

const serializer = {
  read: raw => JSON.parse(raw),
  write: raw => JSON.stringify(raw),
}

export const saved = useSessionStorage('router-state', null, { serializer })

export const useRouteState = (event, route) => {
  const get = () => {
    if (!route?.fullPath) {
      return
    }
    const restoreObj = saved.value?.[route.fullPath]

    if (!restoreObj) {
      return
    }

    if (!restoreObj[event]) {
      return
    }

    const state = { ...restoreObj[event] }
    delete restoreObj[event]

    const val = { ...saved.value }

    if (!Object.keys(restoreObj).length) {
      delete val[route.fullPath]
    } else {
      val[route.fullPath] = restoreObj
    }

    saved.value = val

    return state
  }
  const set = value => {
    if (!route?.fullPath) {
      return
    }
    let restoreObj = saved.value?.[route.fullPath]

    if (!restoreObj) {
      restoreObj = {}
    }

    merge(restoreObj, { [event]: value })

    saved.value = {
      ...saved.value,
      [route.fullPath]: restoreObj,
    }
  }
  return [get, set]
}

const usedialog = (type, { emphasizeYes = true } = {}) =>
  function (msg) {
    const { reveal, cancel, confirm, onCancel, onConfirm, onReveal } = useConfirmDialog()
    let el
    let vnode
    // Да/Нет вызывают confirm/cancel → hide() → settled=true.
    // Крестик, backdrop и Esc закрывают Mod напрямую: в onHidden settled ещё false → cancel(),
    // иначе await reveal() не завершится (бесконечный blockScreen на форме).
    let settled = false

    onCancel(hide)
    onConfirm(hide)

    onReveal(() => {
      settled = false
      el = document.createElement('div')
      // type 1 — только ОК; 2 — Да/Нет; 3 — ОК/Отмена (AS-2155 Alternative name).
      const primaryLabel =
        type === 2 ? i18n.global.t('common.yes') : i18n.global.t('common.ok')
      const cancelLabel =
        type === 2 ? i18n.global.t('common.no') : i18n.global.t('common.cancel')
      const primaryBtn = h(
        'button',
        {
          type: 'button',
          class: emphasizeYes
            ? 'btn page-header-btn page-header-btn--primary'
            : 'btn page-header-btn page-header-btn--secondary',
          onClick: confirm,
        },
        [h('i', { class: 'bi bi-check-lg', 'aria-hidden': 'true' }), ` ${primaryLabel}`]
      )
      const cancelBtn =
        type === 1
          ? null
          : h(
              'button',
              {
                type: 'button',
                class: 'btn page-header-btn page-header-btn--secondary',
                onClick: cancel,
              },
              [h('i', { class: 'bi bi-x-lg', 'aria-hidden': 'true' }), ` ${cancelLabel}`]
            )
      vnode = createVNode(
        Mod,
        {
          header: true,
          close: true,
          useHistory: false,
          closeAriaLabel: i18n.global.t('common.close'),
          ['class-name']: 'confirm-modal',
          onHidden,
        },
        {
          default: () => toValue(msg),
          header: () =>
            h('h1', { class: 'confirm-modal__title' }, i18n.global.t('common.confirmTitle')),
          footer: () =>
            h(
              'div',
              { class: 'confirm-modal__actions' },
              [cancelBtn, primaryBtn].filter(Boolean)
            ),
        }
      )
      render(vnode, el)
    })

    return async () => {
      const { isCanceled } = await reveal()
      return !isCanceled
    }

    function hide() {
      settled = true
      vnode?.component?.exposed?.hide()
    }

    function onHidden() {
      if (!settled) {
        cancel()
      }
      render(null, el)
      vnode = null
      el = null
    }
  }

export const useConfirm = usedialog(2)
/** ОК / Отмена — для выбора занятого Alternative name (AS-2155). */
export const useConfirmOkCancel = usedialog(3)
export const useAlert = usedialog(1)

let deleteSubject = ''
const revealDelete = usedialog(2, { emphasizeYes: false })(() => {
  const name = String(deleteSubject || '').trim()
  return name
    ? i18n.global.t('common.confirmDeleteNamed', { name })
    : i18n.global.t('common.confirmDelete')
})

/**
 * Подтверждение удаления. В тексте — что именно удаляется.
 * Кнопка «Да» без акцента основной кнопки.
 *
 * @param {string} [subject] название объекта на языке интерфейса.
 * @returns {Promise<boolean>} true, если пользователь подтвердил.
 */
export async function useDeleteConfirm(subject) {
  deleteSubject = subject == null ? '' : String(subject)
  try {
    return await revealDelete()
  } finally {
    deleteSubject = ''
  }
}

/** Подтверждение выхода из режима редактирования (Form, submission zones, route leave). */
const confirmExitEditDialog = useConfirm(() => i18n.global.t('common.confirmExitEdit'))
export async function confirmExitEdit() {
  // Меню custom-select на body иначе остаётся поверх confirm (Back браузера).
  closeAllCustomSelects()
  return confirmExitEditDialog()
}

/**
 * Маршруты R7 (просмотрщик / редактор): уход с карточки не считается выходом из редактирования.
 *
 * @param {object} [route] объект маршрута vue-router (`to` / `from`).
 * @returns {boolean} true, если это docview/docedit.
 */
export function isDocViewerRoute(route) {
  const name = route?.name
  return (
    name === 'soykajs.docview' ||
    name === 'soykajs.docedit' ||
    name === 'soykajs.docedit.mention'
  )
}

/** Черновик полей карточки на время ухода в R7 (карточка размонтируется). */
const editSessionStash = new Map()

/**
 * Сохранить локальные правки карточки перед переходом в docview/R7.
 *
 * @param {string} entityPath `products/123`
 * @param {object} payload сериализуемый снимок (model + UI-поля)
 */
export function stashEditSession(entityPath, payload) {
  const key = String(entityPath || '')
  if (!key || payload == null) {
    return
  }
  try {
    editSessionStash.set(key, structuredClone(payload))
  } catch {
    try {
      editSessionStash.set(key, JSON.parse(JSON.stringify(payload)))
    } catch {
      /* circular / File — не блокируем переход в R7 */
    }
  }
}

/**
 * Забрать и удалить снимок правок после возврата на карточку.
 *
 * @param {string} entityPath `products/123`
 * @returns {object|null}
 */
export function takeEditSession(entityPath) {
  const key = String(entityPath || '')
  if (!key || !editSessionStash.has(key)) {
    return null
  }
  const value = editSessionStash.get(key)
  editSessionStash.delete(key)
  return value
}

let editLeaveConfirmSkip = null

/**
 * Пометить ближайший leave с карточки как «без confirm» (например, redirect после create).
 * В отличие от перехода в R7, lock всё равно снимается.
 *
 * @param {string} entityPath путь сущности (`entityName/id`).
 */
export function markEditLeaveConfirmSkipped(entityPath) {
  editLeaveConfirmSkip = {
    entityPath: String(entityPath || ''),
    expiresAt: Date.now() + 5000,
  }
}

/** Ближайший уход с карточки после удаления — без «выйти из редактирования». */
export function markNextEditLeaveConfirmSkipped() {
  editLeaveConfirmSkip = {
    entityPath: '*',
    expiresAt: Date.now() + 5000,
  }
}

/**
 * Съесть одноразовый skip confirm для leave с карточки.
 *
 * @param {string} entityPath путь сущности (`entityName/id`).
 * @returns {boolean} true, если confirm нужно пропустить.
 */
export function consumeEditLeaveConfirmSkipped(entityPath) {
  if (!editLeaveConfirmSkip || editLeaveConfirmSkip.expiresAt < Date.now()) {
    editLeaveConfirmSkip = null
    return false
  }

  const shouldSkip =
    editLeaveConfirmSkip.entityPath === '*'
    || editLeaveConfirmSkip.entityPath === String(entityPath || '')
  if (shouldSkip) {
    editLeaveConfirmSkip = null
  }
  return shouldSkip
}

export const useDateCreatedLabel = model =>
  computed(() => (model.date_created ? commons.dateTimeLocal(model.date_created) : null))
